<template>
  <div class="candidate-list">
    <div class="section-header">
      <h3 class="section-title">Candidates</h3>
      <div class="candidate-count">{{ candidates.length }} candidates</div>
    </div>
    
    <div class="candidate-scroll-container">
      <div
        v-for="candidate in candidates"
        :key="candidate.id"
        @click="$emit('candidate-select', candidate.id)"
        class="candidate-item"
        :class="getCandidateClass(candidate)"
      >
        <div class="candidate-avatar">
          <div class="avatar-placeholder">
            {{ getInitials(candidate.name) }}
          </div>
          <div 
            v-if="candidate.flagged" 
            class="flag-indicator"
            title="Flagged candidate"
          >
            🚩
          </div>
        </div>
        
        <div class="candidate-info">
          <div class="candidate-name">{{ candidate.name }}</div>
          <div class="candidate-id">{{ candidate.id }}</div>
        </div>
        
        <div class="candidate-progress">
          <div 
            v-if="completionData[candidate.id]"
            class="progress-circle"
            :class="getProgressClass(completionData[candidate.id].percentage)"
            :style="{
              '--progress': completionData[candidate.id].percentage + '%'
            }"
          >
            {{ completionData[candidate.id].percentage }}%
          </div>
          <div v-else class="progress-circle not-started">
            0%
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    candidates: Array,
    currentCandidateId: String,
    completionData: Object
  },
  
  methods: {
    getCandidateClass(candidate) {
      return {
        'active': candidate.id === this.currentCandidateId,
        'flagged': candidate.flagged,
        'completed': this.completionData[candidate.id]?.isComplete
      };
    },
    
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    },
    
    getProgressClass(percentage) {
      if (percentage >= 90) return 'progress-complete';
      if (percentage >= 50) return 'progress-in-progress';
      return 'progress-started';
    }
  }
}
</script>

<style scoped>
.candidate-list {
  flex: 1;
  background: white;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 15px 20px;
  border-bottom: 1px solid #e9ecef;
}

.section-title {
  font-size: 1em;
  font-weight: 600;
  color: #495057;
  margin: 0;
}

.candidate-count {
  font-size: 0.8em;
  color: #6c757d;
}

.candidate-scroll-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.candidate-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 8px;
}

.candidate-item:hover {
  background: #f8f9fa;
}

.candidate-item.active {
  background: #007bff;
  color: white;
}

.candidate-item.flagged {
  border-left: 3px solid #dc3545;
}

.candidate-avatar {
  position: relative;
  flex-shrink: 0;
}

.avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #6c757d;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9em;
}

.candidate-item.active .avatar-placeholder {
  background: white;
  color: #007bff;
}

.flag-indicator {
  position: absolute;
  top: -5px;
  right: -5px;
  font-size: 0.8em;
}

.candidate-info {
  flex: 1;
  min-width: 0;
}

.candidate-name {
  font-weight: 500;
  font-size: 0.95em;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.candidate-id {
  font-size: 0.8em;
  opacity: 0.7;
  font-family: monospace;
}

.candidate-progress {
  flex-shrink: 0;
}

.progress-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
  font-weight: 600;
  position: relative;
  background: conic-gradient(
    #28a745 var(--progress),
    #e9ecef var(--progress)
  );
}

.progress-circle::before {
  content: '';
  position: absolute;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
}

.progress-circle > * {
  position: relative;
  z-index: 1;
}

.candidate-item.active .progress-circle::before {
  background: #007bff;
}

.progress-complete {
  background: conic-gradient(#28a745 var(--progress), #e9ecef var(--progress));
}

.progress-in-progress {
  background: conic-gradient(#ffc107 var(--progress), #e9ecef var(--progress));
}

.progress-started {
  background: conic-gradient(#6c757d var(--progress), #e9ecef var(--progress));
}

.not-started {
  background: #e9ecef;
  color: #6c757d;
}
</style>